<template>
    <main class="main__login__page updated__login__page">
        <div class="signin__flow__individual__wrap">
            <div class="signin__title__box">
                <h4 class="title">Sign in to Account</h4>
                <div class="short__note">
                    <p>Sign Into EESAPP App and start conducting the Early Echoic Skills Assessment</p>
                </div>
            </div>

            <div class="signin__form__wrapper">
                <form @submit.prevent="onSubmit">
                    <div class="form__input__field">
                        <label>Enter Your Email Address</label>
                        <input type="email" placeholder="Enter Email" v-model.trim="email" @input="email = $event.target.value.toLowerCase()" />
                        <span class="error__message" v-if="submitted && emailError">{{ emailError }}</span>
                    </div>

                    <div class="form__input__field">
                        <label for="password">Enter Your Password</label>
                        <input type="password" placeholder="Enter your password" v-model.trim="password"
                            autocomplete="current-password" />
                        <span class="error__message" v-if="submitted && passwordError">{{ passwordError
                        }}</span>
                    </div>

                    <div class="form__forgot">
                        <router-link to="/forgot-password">Forgot Password?</router-link>
                    </div>

                    <div class="form__sign__btn">
                        <button type="submit" class="sign__in__btn" :disabled="submitting">
                            Sign In
                        </button>
                    </div>

                    <div class="or"><span>or</span></div>

                    <div class="continue__with__google">
                        <a href="javascript:void(0);" @click="continueWithGoogle">
                            <span class="google__icon">
                                <img :src="googleIcon" alt="Google icon" />
                            </span>
                            <span class="continue__google__text">Continue with Google</span>
                        </a>
                    </div>

                    <div class="signin__options__box">
                        <span class="signin__label">Need an EESA APP Account?</span>
                        <span class="create__account__link">
                            <router-link to="/register">Create an account</router-link>
                        </span>
                    </div>
                </form>
            </div>
        </div>
    </main>
</template>

<script setup>
import { capitalize, onMounted, ref } from 'vue';
import { useForm, useField } from 'vee-validate'
import * as yup from 'yup'
import googleIcon from '@/assets/images/google.svg'
import { useAuthStore } from '@/stores/auth';
import { useRoute, useRouter } from 'vue-router';
import { showDialog, showToast } from '@/utils/swal';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';

const submitting = ref(false);
const submitted = ref(false);
const googleLoginUrl = (process.env.VUE_APP_API_BASE_URL || '/api') + '/auth/google/redirect';
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const status = route.query.status;
const message = route.query.message;
const token = route.query.token;

const loader = useLoaderStore();

// Define Yup validation schema
const schema = yup.object({
    email: yup.string().email('Please enter a valid email.').required('Please enter your email.').test(
        'has-dot-in-domain',
        'Please enter a valid email.',
        (value) => {
            if (!value) return false;
            const domain = value.split('@')[1];
            return domain && domain.includes('.');
        }
    ),
    password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
})

// Initialize vee-validate form with schema
const { handleSubmit, setErrors } = useForm({
    validationSchema: schema,
})

// Setup fields
const { value: email, errorMessage: emailError } = useField('email')
const { value: password, errorMessage: passwordError } = useField('password')

// Form submit handler
const onSubmit = async () => {
    submitted.value = true;

    await handleSubmit(async (values) => {
        submitting.value = true;
        loader.show();
        try {
            await authStore.login(values.email, values.password);

            showToast({
                icon: 'success',
                title: 'Welcome! ',
                text: "You're in! Great to see you again.",
            });

            router.push('/dashboard');

        } catch (error) {
            const message = error?.response?.data?.errors?.email?.[0];

            if (message.startsWith('account_deleted::')) {
                const token = message.split('::')[1];

                router.push(`/restore-account/${token}`);
                return;
            }

            if (error.response?.status === 422) {
                const serverErrors = error?.response?.data?.errors || {};
                const fieldErrors = {};

                Object.keys(serverErrors).forEach(key => {
                    fieldErrors[key] = serverErrors[key][0];
                });

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                } else {
                    handleError(error, true);
                }
            } else {
                handleError(error);
            }
        } finally {
            submitting.value = false;
            loader.hide();
        }
    })();
};

const continueWithGoogle = async () => {
    window.location.href = googleLoginUrl;
}

const handleGoogleLogin = async () => {
    try {
        loader.show();
        await authStore.googleLogin(token);

        router.push('/dashboard');
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

onMounted(async () => {
    if (token && status === 'success') {
        await handleGoogleLogin();
    } else if (status) {
        showDialog({
            title: capitalize(status),
            html: decodeURIComponent(message) ?? 'Something went wrong. Please try again later!',
            icon: status,
        });

        router.push('/login');
    }
});
</script>